import { createContext, useContext, useState } from "react";
import { doctorsDetailList } from "../utils/constants";
import { authenticateApp } from "./auth-context";
import { diseaseAPI } from "../apis";
import { transformDoctorData } from "../utils/eira/helper";

interface IAppointmentContext {
  doctorDb: any;
  setDoctorDb: (value: any) => void;
  bookDoctor: any;
  setBookDoctor: (value: any) => void;
  bookedHistory: any;
  setBookedHistory: (value: any) => void;
  recommendedDoctors: any;
  setRecommendedDoctors: (value: any) => void;
  sortDoctors: (sortingMethod: string, searchTerm: string) => void;
  doctorBySpecialization: any;
  setDoctorBySpecialization: (value: any) => void;
  doctorBySymptoms: any;
  setDoctorBySymptoms: (value: any) => void;
  upcommingAppointments: any;
  setUpcommingAppointments: (value: any) => void;
  // New API-related states and functions
  isLoadingDoctors: boolean;
  setIsLoadingDoctors: (value: boolean) => void;
  doctorsError: any;
  setDoctorsError: (value: any) => void;
  isBookingAppointment: boolean;
  setIsBookingAppointment: (value: boolean) => void;
  bookingError: any;
  setBookingError: (value: any) => void;
  isLoadingAppointments: boolean;
  setIsLoadingAppointments: (value: boolean) => void;
  appointmentsError: any;
  setAppointmentsError: (value: any) => void;
  apiDoctorsList: any[];
  setApiDoctorsList: (value: any[]) => void;
  fetchDoctors: () => Promise<any[]>;
  bookAppointment: (appointmentDetails: any) => Promise<any>;
  fetchUserAppointments: (userMobile: string) => Promise<any[]>;
}

export const AppointmentContext = createContext<IAppointmentContext>(
  {} as IAppointmentContext
);

const useAppointmentProvider = () => {
  const { clinicUserId } = authenticateApp();
  const [doctorDb, setDoctorDb] = useState<any>(doctorsDetailList);
  const [bookDoctor, setBookDoctor] = useState<any>([]);
  const [bookedHistory, setBookedHistory] = useState<any>([
    doctorsDetailList[0],
    // doctorsDetailList[1],
    // doctorsDetailList[2],
  ]);
  const [upcommingAppointments, setUpcommingAppointments] = useState<any>([]);

  const [recommendedDoctors, setRecommendedDoctors] = useState<any>([
    doctorsDetailList[0],
  ]);

  const [doctorBySpecialization, setDoctorBySpecialization] = useState<any>([
    {
      docSpecNeeded: ["General Physician", "Internal Medicine"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.specialization.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["General Physician", "Internal Medicine"].includes(spec)
        )
      ),

      label: "General Physician/ Internal Medicine",
    },
    {
      docSpecNeeded: ["Obstetrician", "Gynaecologist"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.specialization.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Obstetrician", "Gynaecologist"].includes(spec)
        )
      ),
      label: "Gynae & Obs",
    },
    {
      docSpecNeeded: ["Orthopaedician"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.specialization.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Orthopaedician"].includes(spec)
        )
      ),
      label: "Orthopaedician",
    },

    {
      docSpecNeeded: ["Neurologist"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.specialization.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Neurologist"].includes(spec)
        )
      ),
      label: "Neurologist",
    },
    {
      docSpecNeeded: ["Cardiologist"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.specialization.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Cardiologist"].includes(spec)
        )
      ),
      label: "Cardiologist",
    },
    {
      docSpecNeeded: ["Allergist and Clinical Immunologist"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.specialization.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Allergist and Clinical Immunologist"].includes(spec)
        )
      ),
      label: "Allergist and Clinical Immunologist",
    },
    {
      docSpecNeeded: ["Anaesthesia"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.specialization.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Anaesthesia"].includes(spec)
        )
      ),
      label: "Anaesthesia",
    },
  ]);

  const [doctorBySymptoms, setDoctorBySymptoms] = useState<any>([
    {
      docSpecNeeded: ["Coronavirus"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.symptoms.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Coronavirus"].includes(spec)
        )
      ),

      label: "Coronavirus",
    },
    {
      docSpecNeeded: ["Headache"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.symptoms.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Headache"].includes(spec)
        )
      ),
      label: "Headache",
    },
    {
      docSpecNeeded: ["Acidity"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.symptoms.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Acidity"].includes(spec)
        )
      ),
      label: "Acidity",
    },

    {
      docSpecNeeded: ["Indigestion"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.symptoms.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Indigestion"].includes(spec)
        )
      ),
      label: "Indigestion",
    },
    {
      docSpecNeeded: ["Irregular Periods"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.symptoms.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Irregular Periods"].includes(spec)
        )
      ),
      label: "Irregular Periods",
    },
    {
      docSpecNeeded: ["Hypertension"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.symptoms.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Hypertension"].includes(spec)
        )
      ),
      label: "Hypertension",
    },
    {
      docSpecNeeded: ["Breathing Problems"],
      doctors: doctorsDetailList.filter((doctor: any) =>
        doctor.symptoms.some((spec: string) =>
          // Check if doctor has any specialization matching the ones in docSpecNeeded array
          ["Breathing Problems"].includes(spec)
        )
      ),
      label: "Breathing Problems",
    },
  ]);

  const sortDoctors = (sortingMethod: string, searchTerm: string) => {
    let sortedDoctors = [...doctorsDetailList]; // Create a copy of the doctorDb to avoid direct mutation

    // Search functionality
    if (sortingMethod === "search" && searchTerm) {
      sortedDoctors = sortedDoctors.filter((doctor: any) => {
        const searchLower = searchTerm.toLowerCase(); // Make search case-insensitive
        return (
          doctor.name.toLowerCase().includes(searchLower) ||
          doctor.specialization.some((spec: string) =>
            spec.toLowerCase().includes(searchLower)
          ) ||
          doctor.experience.toString().includes(searchLower) ||
          doctor.qualifications.toLowerCase().includes(searchLower) ||
          doctor.location.toLowerCase().includes(searchLower) ||
          doctor.clinic.toLowerCase().includes(searchLower) ||
          doctor.rating.toString().includes(searchLower)
        );
      });
    } else if (sortingMethod === "availability") {
      sortedDoctors.sort((a: any, b: any) => a.availability - b.availability);
    } else if (sortingMethod === "nearby") {
      sortedDoctors.sort((a: any, b: any) => a.distance - b.distance);
    } else if (sortingMethod === "priceLowToHigh") {
      sortedDoctors.sort((a: any, b: any) => a.fee - b.fee); // Fixed the price field name to 'fee'
    } else if (sortingMethod === "priceHighToLow") {
      sortedDoctors.sort((a: any, b: any) => b.fee - a.fee);
    } else if (sortingMethod === "ratings") {
      sortedDoctors.sort((a: any, b: any) => b.rating - a.rating); // Fixed the field name to 'rating'
    } else {
      setDoctorDb(doctorsDetailList);
    }

    setDoctorDb(sortedDoctors); // Update the doctorDb with the sorted list
  };

  // New API-related states
  const [isLoadingDoctors, setIsLoadingDoctors] = useState<boolean>(false);
  const [doctorsError, setDoctorsError] = useState<any>(null);
  const [isBookingAppointment, setIsBookingAppointment] =
    useState<boolean>(false);
  const [bookingError, setBookingError] = useState<any>(null);
  const [isLoadingAppointments, setIsLoadingAppointments] =
    useState<boolean>(false);
  const [appointmentsError, setAppointmentsError] = useState<any>(null);
  const [apiDoctorsList, setApiDoctorsList] = useState<any[]>([]);

  // Function to transform API doctor data to match existing format

  // Function to fetch all doctors from API
  const fetchDoctors = async () => {
    setIsLoadingDoctors(true);
    setDoctorsError(null);
    try {
      // Original API call (commented out)
      // const response = await diseaseAPI.getAllDcotors();

      // Dummy data as replacement for API call
      const response = {
        data: {
          rows: [
            {
              userId: 8,
              displayName: null,
              firstName: "Khushi",
              lastName: "M",
              enabled: true,
              email: "khushi@wemotiveforge.com",
              phone: "+912343454566",
              active: true,
              npi: "",
              team: "test",
              role: "Receptionist",
              isBanned: false,
              teamId: 2,
              teamDetails: {
                id: 2,
                headDoctor: false,
                createdAt: "2025-04-24T11:04:33.000Z",
                updatedAt: "2025-04-24T11:04:33.000Z",
                TeamId: 1,
                UserId: 8,
                Team: {
                  id: 1,
                  name: "Eye",
                  desc: "Eye Desc",
                  imageUrl: "",
                  system: null,
                  vendorId: null,
                  active: true,
                  createdAt: "2025-04-24T10:45:43.000Z",
                  updatedAt: "2025-04-24T10:45:43.000Z",
                  HealthcareorgId: null,
                  OrganizationId: 4,
                  DepartmentTypeId: 2,
                },
              },
              imageUrl: "",
            },
            {
              userId: 7,
              displayName: null,
              firstName: "gold",
              lastName: "man",
              enabled: true,
              email: "test@gmil.COM",
              phone: "+914987654323",
              active: true,
              npi: "",
              team: "test",
              role: "Doctor",
              isBanned: false,
              teamId: 1,
              teamDetails: {
                id: 1,
                headDoctor: false,
                createdAt: "2025-04-24T10:53:38.000Z",
                updatedAt: "2025-04-24T10:53:38.000Z",
                TeamId: 1,
                UserId: 7,
                Team: {
                  id: 1,
                  name: "Eye",
                  desc: "Eye Desc",
                  imageUrl: "",
                  system: null,
                  vendorId: null,
                  active: true,
                  createdAt: "2025-04-24T10:45:43.000Z",
                  updatedAt: "2025-04-24T10:45:43.000Z",
                  HealthcareorgId: null,
                  OrganizationId: 4,
                  DepartmentTypeId: 2,
                },
              },
              imageUrl: "",
            },
          ],
        },
      };

      const apiDoctors = response.data.rows || [];
      const transformedDoctors = transformDoctorData(apiDoctors);

      // Set the transformed doctors to the API doctors list
      setApiDoctorsList(transformedDoctors);

      // Optionally update doctorDb with API data
      // Uncomment the following line to replace existing doctors with API data
      // setDoctorDb(transformedDoctors);

      return transformedDoctors;
    } catch (error) {
      console.error("Error fetching doctors:", error);
      setDoctorsError(error);
      return [];
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Function to transform API appointment data
  const transformAppointmentData = (apiAppointments: any[]) => {
    return apiAppointments.map((apt: any) => {
      // Find the doctor in apiDoctorsList or doctorDb
      const doctor =
        apiDoctorsList.find((doc) => doc.id === apt.userId) ||
        doctorDb.find((doc: any) => doc.id === apt.userId);

      return {
        id: apt.id,
        date: new Date(apt.appointmentDate),
        startTime: new Date(apt.startTime),
        endTime: new Date(apt.endTime),
        title: apt.title,
        doctor: doctor || null,
        doctorId: apt.userId,
        teamId: apt.teamId,
        patientInfo: apt.patient || {},
        status: apt.status || "scheduled",
        appointmentLink: apt.appointmentLink || "",
      };
    });
  };

  // Function to fetch appointments for a user
  const fetchUserAppointments = async (userMobile: string) => {
    setIsLoadingAppointments(true);
    setAppointmentsError(null);
    try {
      // Make sure the number is properly formatted
      const formattedMobile = userMobile.startsWith("+")
        ? userMobile
        : `+${userMobile}`;

      const response = await diseaseAPI.getAllAppointmentsBookedForUser(
        clinicUserId!
      );
      const appointments = response.data || [];

      // Transform appointments to match the expected format
      const formattedAppointments = transformAppointmentData(appointments);

      // Update upcoming appointments
      setUpcommingAppointments(formattedAppointments);

      return formattedAppointments;
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setAppointmentsError(error);
      return [];
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  // Function to book an appointment
  const bookAppointment = async (appointmentDetails: any) => {
    setIsBookingAppointment(true);
    setBookingError(null);
    try {
      const response = await diseaseAPI.bookAppointment(appointmentDetails);
      // After successful booking, get the updated appointments
      if (appointmentDetails.patient && appointmentDetails.patient.mobile) {
        await fetchUserAppointments(appointmentDetails.patient.mobile);
      }

      return response;
    } catch (error) {
      console.error("Error booking appointment:", error);
      setBookingError(error);
      throw error;
    } finally {
      setIsBookingAppointment(false);
    }
  };

  return {
    doctorDb,
    setDoctorDb,
    bookDoctor,
    setBookDoctor,
    bookedHistory,
    setBookedHistory,
    recommendedDoctors,
    setRecommendedDoctors,
    sortDoctors,
    doctorBySpecialization,
    setDoctorBySpecialization,
    doctorBySymptoms,
    setDoctorBySymptoms,
    upcommingAppointments,
    setUpcommingAppointments,
    // New API states and functions
    isLoadingDoctors,
    setIsLoadingDoctors,
    doctorsError,
    setDoctorsError,
    isBookingAppointment,
    setIsBookingAppointment,
    bookingError,
    setBookingError,
    isLoadingAppointments,
    setIsLoadingAppointments,
    appointmentsError,
    setAppointmentsError,
    apiDoctorsList,
    setApiDoctorsList,
    fetchDoctors,
    bookAppointment,
    fetchUserAppointments,
  };
};

interface Props {
  children: React.ReactNode;
}

export const AppointmentProvider = ({ children }: Props) => {
  const Appointment = useAppointmentProvider();

  return (
    <AppointmentContext.Provider value={Appointment}>
      {children}
    </AppointmentContext.Provider>
  );
};

export const useAppointmentState = () => {
  return useContext(AppointmentContext);
};
